import { ReactElement, useState, useEffect } from 'react';

import { SolutionSessionMessage } from '@backrs_ui/grow-ui/src/models/models';
import {
  useCreateEngagementHistoryMutation,
  useUpdateEngagementHistoryMutation,
  useGetUserEngagementHistoryQuery,
  UserEngagementHistoryRequest,
} from '@backrs_ui/grow-ui/src/redux/userPortfolio/userEngagementHistory.api';

import {
  prePopulateFormWithEngagementHistory,
  buildEngagementHistoryFromFormValues,
} from './engagement.utils';
import { ENGAGEMENT_FIELDS } from './questions.config';
import { ResumeQuestionForm } from './ResumeQuestionForm';
import { FormValue, FormValues } from './types';

type EngagementQuestionProps = Readonly<{
  sendQAMessage: (message: string, LLMInput?: string) => void;
  questionNumber?: number;
  message?: SolutionSessionMessage;
}>;

export const EngagementQuestion = ({
  sendQAMessage,
  questionNumber = 1,
  message,
}: EngagementQuestionProps): ReactElement => {
  const [createEngagementHistory] = useCreateEngagementHistoryMutation();
  const [updateEngagementHistory] = useUpdateEngagementHistoryMutation();
  const [engagementHistoryId, setEngagementHistoryId] = useState<number | null>(null);
  const [initialValues, setInitialValues] = useState<FormValues>({});

  const { data: existingEngagementHistory, refetch: refetchEngagementHistory } =
    useGetUserEngagementHistoryQuery(undefined);

  useEffect(() => {
    if (message) {
      refetchEngagementHistory();
    }
  }, [message, refetchEngagementHistory]);

  useEffect(() => {
    if (existingEngagementHistory && existingEngagementHistory.length > 0) {
      const result = prePopulateFormWithEngagementHistory(
        existingEngagementHistory,
        questionNumber,
      );

      if (result) {
        setEngagementHistoryId(result.engagementHistoryId);
        setInitialValues(result.formValues);
      }
    }
  }, [existingEngagementHistory, questionNumber]);

  const handleSubmit = async (formValues: FormValues) => {
    const engagementHistoryData = buildEngagementHistoryFromFormValues(formValues);

    if (Object.keys(engagementHistoryData).length === 0) return;

    try {
      if (engagementHistoryId) {
        await updateEngagementHistory({
          id: engagementHistoryId,
          data: engagementHistoryData as UserEngagementHistoryRequest,
        }).unwrap();
      } else {
        await createEngagementHistory(
          engagementHistoryData as UserEngagementHistoryRequest,
        ).unwrap();
      }
    } catch (error) {
      console.error('Error updating engagement history:', error);
    }
  };

  const getDefaultValue = (
    fieldKey: string,
    formValues: FormValues,
  ): FormValue | FormValue[] | undefined => {
    if (!formValues[fieldKey] || formValues[fieldKey].length === 0) return undefined;

    switch (fieldKey) {
      default:
        return formValues[fieldKey];
    }
  };

  return (
    <ResumeQuestionForm
      fields={ENGAGEMENT_FIELDS}
      sendQAMessage={sendQAMessage}
      onSubmit={handleSubmit}
      initialValues={initialValues}
      getDefaultValue={getDefaultValue}
    />
  );
};
